import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { getMyAccess } from '../services/modulePermissions.service';

interface ModulePermissionsContextType {
  permissions: Record<string, boolean>;
  isModuleAllowed: (key: string) => boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const ModulePermissionsContext = createContext<ModulePermissionsContextType | null>(null);

export function ModulePermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions({});
      return;
    }
    // super_admin has no module restrictions — skip the API call
    if (user.role === 'super_admin') {
      setPermissions({});
      return;
    }
    setIsLoading(true);
    try {
      const perms = await getMyAccess();
      setPermissions(perms);
    } catch {
      setPermissions({});
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchPermissions();
  }, [fetchPermissions]);

  const isModuleAllowed = useCallback(
    (key: string): boolean => {
      if (!user) return false;
      if (user.role === 'super_admin') return true;
      // Fail-open: if no entry for this key, allow it (matches backend behaviour)
      return permissions[key] ?? true;
    },
    [user, permissions],
  );

  return (
    <ModulePermissionsContext.Provider
      value={{ permissions, isModuleAllowed, isLoading, refresh: fetchPermissions }}
    >
      {children}
    </ModulePermissionsContext.Provider>
  );
}

export function useModulePermissions(): ModulePermissionsContextType {
  const ctx = useContext(ModulePermissionsContext);
  if (!ctx) throw new Error('useModulePermissions must be used within ModulePermissionsProvider');
  return ctx;
}
